import React, { FC } from 'react'
import DownArrowIcon from 'transfix-icons/build/DownArrowIcon'
import { makeStyles, Menu, MenuItem } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { FLEET_RANGE } from 'apps/fleet-planner/types'
import { Button } from 'transfix-ui'

const useCalendarRangeSizeStyles = makeStyles(theme => ({
  button: {
    color: brandColors.black,
    marginRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
  },
}))

interface IRangeOption {
  label: string
  value: FLEET_RANGE
}

const rangeOptions: IRangeOption[] = [
  { label: '1 Day', value: FLEET_RANGE.ONE_DAY },
  { label: '3 Days', value: FLEET_RANGE.THREE_DAYS },
  { label: 'Week', value: FLEET_RANGE.WEEK },
]

const rangeOptionsMobile: IRangeOption[] = [
  { label: '1 Day', value: FLEET_RANGE.ONE_DAY },
  { label: '2 Days', value: FLEET_RANGE.TWO_DAYS },
]

interface ICalendarRangeSizeProps {
  rangeSize: FLEET_RANGE
  onRangeSizeChange(rangeSize: FLEET_RANGE): void
}

export const CalendarRangeSize: FC<ICalendarRangeSizeProps> = ({
  onRangeSizeChange,
  rangeSize,
}) => {
  const { isMobile } = useBreakpointContext()
  const classes = useCalendarRangeSizeStyles()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  const isOpen = Boolean(anchorEl)
  const buttonAriaControls = isOpen ? 'calendar-range-size-menu' : undefined
  const buttonAriaExpanded = isOpen ? 'true' : undefined
  const options = isMobile ? rangeOptionsMobile : rangeOptions
  const selectedOption = options.find(({ value }) => value === rangeSize)
  const buttonLabel = selectedOption?.label || ''

  const clickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const menuCloseHandler = () => {
    setAnchorEl(null)
  }

  return (
    <div>
      <Button
        id='calendar-range-size-button'
        aria-controls={buttonAriaControls}
        aria-haspopup='true'
        aria-expanded={buttonAriaExpanded}
        className={classes.button}
        endIcon={<DownArrowIcon color='skyBlue6' />}
        onClick={clickHandler}
        color={isMobile ? 'tertiary' : 'secondary'}>
        {buttonLabel}
      </Button>

      <Menu
        id='calendar-range-size-menu'
        anchorEl={anchorEl}
        open={isOpen}
        onClose={menuCloseHandler}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        MenuListProps={{
          'aria-labelledby': 'calendar-range-size-button',
        }}>
        {options.map(option => {
          const { label, value } = option
          const isSelected = value === selectedOption?.value

          const itemClickHandler = () => {
            menuCloseHandler()
            onRangeSizeChange(value)
          }

          return (
            <MenuItem key={value} selected={isSelected} value={value} onClick={itemClickHandler}>
              {label}
            </MenuItem>
          )
        })}
      </Menu>
    </div>
  )
}
